{
  Container
  Rectangle
  Sprite
  Text
  Texture
  BLEND_MODES
} from "pixi.js"

{ceil, cos, floor, min, PI, random, sin} := Math

// Hit-burst particles for the practice/game overlays in both views, carrying
// over what the chip arrange-lab effects settled (chip/source/note-fx.mjs):
// one pooled sprite layer per view with a hard cap, glyphs off a one-time
// white atlas tinted per use, additive blending for the pop, and closed-form
// motion — a particle is its spawn record and position falls out of its age,
// so stepping is cheap and nothing accumulates. Unlike chip's render path
// this is live-only, so wall-clock time and Math.random are fine here.

SHAPES := ["dot", "star", "shard"] as const
CELL := 48

// White glyphs drawn once at CELL px and scaled down — the star's spikes stay
// smooth at any particle size. Same construction as chip's makeSparkAtlas.
makeSparkTextures := ->
  canvas := document.createElement "canvas"
  canvas.width = CELL * SHAPES.length
  canvas.height = CELL
  ctx := canvas.getContext("2d")!
  r := CELL / 2

  SHAPES.forEach (shape, i) ->
    cx := i * CELL + r
    ctx.save()
    ctx.translate cx, r
    ctx.fillStyle = "#ffffff"
    switch shape
      when "dot"
        g := ctx.createRadialGradient 0, 0, 0, 0, 0, r
        g.addColorStop 0, "rgba(255,255,255,1)"
        g.addColorStop 0.4, "rgba(255,255,255,0.55)"
        g.addColorStop 1, "rgba(255,255,255,0)"
        ctx.fillStyle = g
        ctx.beginPath()
        ctx.arc 0, 0, r, 0, PI * 2
        ctx.fill()
      when "star"
        // Four-point sparkle: concave sides so the spikes taper to a point
        ctx.beginPath()
        for k of [0...4]
          a := k * PI / 2
          nx := cos(a) * r
          ny := sin(a) * r
          if k is 0
            ctx.moveTo nx, ny
          else
            ctx.quadraticCurveTo 0, 0, nx, ny
        ctx.quadraticCurveTo 0, 0, r, 0
        ctx.fill()
      when "shard"
        // Thin sliver, reads as a fleck of debris
        ctx.beginPath()
        ctx.moveTo(-r * 0.15, -r)
        ctx.lineTo(r * 0.15, 0)
        ctx.lineTo(-r * 0.15, r)
        ctx.closePath()
        ctx.fill()
    ctx.restore()

  base := Texture.from(canvas).baseTexture
  SHAPES.map (_, i) ->
    new Texture base, new Rectangle i * CELL, 0, CELL, CELL

type Particle =
  sprite: Sprite
  x: number
  y: number
  vx: number
  vy: number
  born: number
  life: number // seconds
  size: number
  spin: number

GRAVITY := 900 // px/s²

// A pooled burst emitter parented to `parent`. Coordinates are the parent's
// own space, so the roll spawns in screen space and the staff spawns in its
// scrolling beat space and both just work.
//
// `additive` suits a dark field (the roll), where the sparks read as light.
// Over a light field it self-erases — white plus anything is white — so the
// staff passes false and gets opaque flecks instead.
export BurstLayer := (parent: Container, {cap = 96, additive = true} = {}) ->
  let textures: Texture[]?
  layer := new Container
  parent.addChild layer

  pool: Sprite[] := []
  active: Particle[] := []
  // the last clock step() ran on; births stamp from it so ages never mix a
  // wall-time birth with a synthetic-clock step (offline renders)
  layerNow .= undefined as number?

  obtain := ->
    s .= pool.pop()
    unless s
      return if layer.children.length >= cap
      textures ?= makeSparkTextures()
      s = new Sprite textures[0]
      s.anchor.set 0.5
      s.blendMode = if additive then BLEND_MODES.ADD else BLEND_MODES.NORMAL
      layer.addChild s
    s.visible = true
    s

  // A fan of sparks from (x, y): mostly upward, judged-color tinted with a
  // white star mixed in every few particles so the flash reads hot.
  spawn := (x: number, y: number, color: number, count: number, speed = 260) ->
    textures ?= makeSparkTextures()
    // births ride the clock step() is driven with (see layerNow) — wall time
    // live, the synthetic frame clock in an offline render
    now := layerNow ?? performance.now()
    for i of [0...count]
      s := obtain()
      break unless s
      // every fourth particle is a bigger star: white-hot on a dark field,
      // judgment-colored on a light one where white would disappear
      white := i % 4 is 3
      s.texture = textures[if white then 1 else floor random() * textures.length]
      s.tint = if white and additive then 0xFFFFFF else color
      a := -PI / 2 + (random() - 0.5) * 1.9 // upward fan, ±~54°
      v := speed * (0.35 + random() * 0.9)
      active.push {
        sprite: s
        x, y
        vx: cos(a) * v
        vy: sin(a) * v
        born: now
        life: 0.45 + random() * 0.35
        size: (0.12 + random() * 0.14) * (if white then 1.6 else 1)
        spin: (random() - 0.5) * 6
      }
    return

  release := (p: Particle) ->
    p.sprite.visible = false
    pool.push p.sprite

  // Advance every live particle to `now`. Closed form from age — no per-frame
  // integration, so a dropped frame just lands further along the same arc.
  step := (now: number) ->
    layerNow = now
    i .= 0
    while i < active.length
      p := active[i]
      age := (now - p.born) / 1000
      if age >= p.life
        release p
        // swap-remove; order among particles doesn't matter
        active[i] = active[active.length - 1]
        active.pop()
        continue
      fade := 1 - age / p.life
      {sprite} := p
      sprite.x = p.x + p.vx * age
      sprite.y = p.y + p.vy * age + 0.5 * GRAVITY * age * age
      sprite.alpha = fade
      sprite.rotation = p.spin * age
      sprite.scale.set p.size * (0.6 + 0.4 * fade)
      i++
    return

  {spawn, step}

// Bursts celebrate the moment of the hit: a feedback entry older than this
// never spawns one. Each view tracks its own seen-through cursor, which goes
// stale while the view isn't rendering — without the age cap, switching
// views would replay every lingering entry's burst on the first frame.
export BURST_MAX_AGE_MS := 200

// A pooled layer of one-line text sprites off a shared texture cache — the
// judgment popups and fingering labels in every view. Call `get` per label
// per frame (undefined once a capped pool is spent), position/tint the
// returned sprite, and `finish` at the end of the frame to hide the rest.
export TextSpritePool := (parent: Container, {fontFamily = "sans-serif", fontSize, cap}: {fontFamily?: string, fontSize: number, cap?: number}) ->
  layer := new Container
  parent.addChild layer
  textures := new Map<string, Texture>
  sprites := [] as Sprite[]
  i .= 0

  texture := (text: string) ->
    t .= textures.get text
    unless t
      label := new Text text, {fontFamily, fontSize, fontWeight: "bold", fill: 0xFFFFFF}
      label.updateText false
      t = label.texture
      textures.set text, t
    t

  get := (text: string) ->
    return if cap? and i >= cap
    s .= sprites[i]
    unless s
      s = new Sprite
      sprites.push s
      layer.addChild s
    i++
    s.texture = texture text
    s.visible = true
    s

  finish := ->
    while i < sprites.length
      sprites[i++].visible = false
    i = 0

  {get, finish}

// The judged-hit spawn ritual every view shares: the tier-scaled primary
// wave, and an echo wave riding it at tier 3+ (white on the rolls' dark
// field; the staff passes the judgment color, where white would vanish).
export spawnJudgedBurst := (
  bursts: ReturnType<typeof BurstLayer>
  x: number
  y: number
  color: number
  judgment: string
  tier: number
  echoColor = 0xFFFFFF
) ->
  spec := burstSpec judgment, tier
  return unless spec
  bursts.spawn x, y, color, spec.count, spec.speed
  if tier >= 3
    bursts.spawn x, y, echoColor, ceil(spec.count / 2), spec.speed * 1.4
  return

// High-combo hit-line glow, 0 outside game mode: the alpha of the wide band
// (the views draw a second band at double it), pulsing faster than the combo
// text so the two shimmer against each other.
export comboGlow := (player: Player, now: number) ->
  mult := player.gameHudMultiplier?.() ?? 1
  return 0 unless mult >= 2 and player.playMode?.() is "game"
  pulse := 0.6 + 0.4 * sin now / 110
  0.05 * (mult - 1) * pulse

// What a judgment is worth in sparks. Misses and wrongs get nothing — their
// feedback is the red flash — and the count grows with the combo tier so a
// hot streak visibly runs hotter (chip's "combo-driven juice").
export burstSpec := (judgment: string, tier = 1) ->
  base := switch judgment
    when "perfect" then 12
    when "good" then 7
    when "early", "late" then 4
    else return
  count: min 24, floor base * (1 + 0.4 * (tier - 1))
  speed: 220 + 50 * tier

export default BurstLayer
